import React, { useEffect, useRef, useState } from "react";
import { api, newIdempotencyKey } from "@/api/client";
import OwnerVoiceConnection from "./OwnerVoiceConnection";
import ProductVoiceConsent from "./ProductVoiceConsent";

export default function VoiceRehearsal({ streamId, enabled = false, owner = false, microphoneReady = false, productCohost = false }) {
  return enabled ? <>{owner ? <OwnerVoiceConnection microphoneReady={microphoneReady} key={`owner-${streamId}`} streamId={streamId} /> : productCohost ? <ProductVoiceConsent key={streamId} streamId={streamId} /> : <Controls key={streamId} streamId={streamId} />}</> : <section aria-label="AI co-host rehearsal" className="mt-4 p-4 tt-panel text-white/80"><h2>Private AI co-host rehearsal</h2><p role="status">AI audio is not enabled in this release. Your microphone stays under your control.</p></section>;
}

function Controls({ streamId }) {
  const [info, setInfo] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fresh, setFresh] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [attempted, setAttempted] = useState(false);
  const alive = useRef(true);
  const consent = useRef(false);
  const key = useRef(null);
  const operating = useRef(false);
  const path = `/livestreams/${encodeURIComponent(streamId)}/voice-rehearsal`;
  const refresh = async () => {
    const next = await api(path, { auth: true });
    if (alive.current) { setInfo(next); setFresh(true); consent.current = next.my_consent; }
    return next;
  };
  const revoke = version => api(`${path}/consent`, {
    auth: true, method: "POST", body: { version, accepted: false },
  });
  const version = useRef(null);

  useEffect(() => {
    alive.current = true;
    let polling = false;
    const poll = async () => {
      if (polling || operating.current) return;
      polling = true;
      try {
        const next = await api(path, { auth: true });
        if (alive.current) { setInfo(next); setFresh(true); consent.current = next.my_consent; version.current = next.version; }
      } catch (e) {
        if (alive.current) { setFresh(false); setError(e.status === 403
          ? "This room is not authorized for AI connection. This is an access gate, not proof of a provider outage. No AI start was made."
          : "AI connection status could not be read. No automatic start was made. You can still stop or withdraw consent."); }
      } finally { polling = false; }
    };
    poll();
    const timer = setInterval(poll, 2000);
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      alive.current = false;
      clearInterval(timer);
      clearInterval(clock);
      if (consent.current && version.current) {
        api(`${path}/consent`, { auth: true, method: "POST", body: { version: version.current, accepted: false } }).catch(() => {});
      }
    };
  }, [path]);

  async function action(kind) {
    if ((operating.current && !["stop", "revoke"].includes(kind)) || !info) return;
    operating.current = true; setBusy(true); setError("");
    try {
      if (kind === "consent") {
        if (!checked || !info.enabled) return;
        consent.current = true;
        await api(`${path}/consent`, { auth: true, method: "POST", body: { version: info.version, accepted: true } });
        if (!alive.current) { await revoke(info.version); return; }
      } else if (kind === "revoke") {
        await revoke(info.version); consent.current = false; setChecked(false);
      } else if (kind === "start") {
        if (!fresh || !info.enabled || !info.is_host || !info.host_consented) return;
        if (info.acceptance && (attempted || !info.can_start || info.consumed)) return;
        if (info.acceptance) setAttempted(true);
        key.current ||= newIdempotencyKey();
        await api(`${path}/start`, { auth: true, method: "POST", timeoutMs: 20000,
          body: { idempotency_key: key.current } });
      } else if (kind === "stop") {
        await api(`${path}/stop`, { auth: true, method: "POST" });
      }
      if (alive.current) await refresh();
    } catch (_) {
      if (alive.current) setError("The request could not be confirmed. Check status before retrying; no automatic start retry was made.");
    } finally {
      operating.current = false;
      if (alive.current) setBusy(false);
    }
  }

  return <section aria-label="AI co-host rehearsal" className="mt-4 p-4 rounded-xl tt-panel text-white/80 text-sm space-y-3">
    <h2 className="font-display text-lg">Private AI co-host rehearsal</h2>
    <p role="status">{!fresh && error ? "AI connection status unavailable." : !info ? "Checking rehearsal availability…" : !info.enabled ? "Rehearsal is disabled." : `Rehearsal: ${info.status}`}</p>
    {info && <>
      <p>{info.notice}</p>
      {info.acceptance ? <>
        <p>One private acceptance session, you only. Maximum {info.limits.total_seconds} seconds including startup and cleanup, {info.limits.max_ai_attempts} AI attempts, and {info.limits.max_reply_characters} characters per reply. No retries. These workload limits are not a monetary spending cap. Consent does not turn on your microphone.</p>
        {info.deadline && info.status !== "finished" && <p role="timer">Time remaining: {Math.max(0, Math.ceil((info.deadline * 1000 - now) / 1000))} seconds</p>}
        {info.consumed && <p>This single-use session is consumed. It cannot be started again.</p>}
        {info.result && <p>Session {info.result.outcome}. Cleanup {info.result.cleanup_confirmed ? "confirmed" : "not confirmed; operator review required"}.</p>}
      </> : <p>Maximum 2 minutes and 4 AI replies. Start with your own microphone. Approved co-hosts may join later after giving their own consent. Consent does not turn on your microphone. Use the existing microphone control when you choose to speak.</p>}
      {!info.my_consent && info.enabled && <>
        <label className="flex gap-2"><input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} />I agree to the audio processing described above for this rehearsal.</label>
        <button disabled={!checked || busy} onClick={() => action("consent")} className="px-3 py-2 rounded border border-white/30 disabled:opacity-40">Give my consent</button>
      </>}
      {info.my_consent && <button onClick={() => action("revoke")} className="px-3 py-2 rounded border border-white/30">Withdraw consent and stop AI</button>}
      {info.enabled && <p>{info.host_consented ? "Host consent is ready." : "The host must consent before starting."}</p>}
      {info.is_host && <div className="flex gap-3">
        <button disabled={busy || !fresh || !info.enabled || !info.host_consented || info.status !== "idle" ||
          (info.acceptance && (attempted || !info.can_start || info.consumed))}
          onClick={() => action("start")} className="px-3 py-2 rounded border border-white/30 disabled:opacity-40">Start AI co-host</button>
        <button onClick={() => action("stop")} className="px-3 py-2 rounded border border-white/30">Stop AI co-host</button>
      </div>}
    </>}
    {error && <p role="alert">{error}</p>}
  </section>;
}
